import { FC, memo, useMemo, useState } from "react";
import styles from "./PublishHackathonModal.module.css";
import { Modal, Checkbox, Form, Space } from "antd";
import { useParams } from "react-router-dom";
import { Button } from "components/Button";
import { usePublishHackathon } from "hooks/usePublishHackathon";
import { useQueryClient } from "react-query";
import { getHackathonRequest, useHackathon } from "api/hackathon";
import { usePublishCondition } from "hooks/usePublishCondition";
import { CHAIN_BY_NETWORK_KEYS } from "utils/constant";

interface ModalProps {
  open: boolean;
  onClose: () => void;
}

const PublishHackathonModal: FC<ModalProps> = ({ open, onClose }) => {
  const { id } = useParams<{ id: string }>();
  const [checkCount, setCheckCount] = useState(0);
  const queryClient = useQueryClient();
  const { data: hackathon } = useHackathon(id, { enabled: false });
  const { canPublish } = usePublishCondition(hackathon);

  const prizeChainId = useMemo(() => {
    if (!hackathon?.prizes?.network) return 1;
    return CHAIN_BY_NETWORK_KEYS[hackathon?.prizes?.network];
  }, [hackathon?.prizes?.network]);

  const { mutate, isLoading, needConnect, chainId } =
    usePublishHackathon(prizeChainId);
  const handlePublish = async () => {
    try {
      const hackathon = await getHackathonRequest(id);
      queryClient.setQueriesData(["hackathon", id], hackathon);
      mutate(id, hackathon);
    } catch (err) {
      console.log(err);
    }
  };

  const label = useMemo(() => {
    const isCryptoHKT = false;

    if (isCryptoHKT && needConnect) return "Connect wallet";

    if (isCryptoHKT && prizeChainId !== chainId) return "Switch network";

    if (hackathon?.status === "draft" || hackathon?.status === "rejected") {
      return "Send to review";
    }

    return hackathon?.status;
  }, [needConnect, hackathon, chainId]);

  return (
    <Modal
      width={600}
      visible={open}
      onCancel={onClose}
      footer={null}
      className={styles.root}
    >
      <h1>One last thing!</h1>
      <div className="mb-24">
        The hacker community is extremely important to us. We only promote high
        quality hackathons and that&apos;s why you&apos;ll find Hackathon
        hackers all over the world. Please show us that you hold yourself to the
        same standards.
      </div>
      <Form name="publish-form" onFinish={handlePublish}>
        <Form.Item>
          <Checkbox.Group
            onChange={(value) => {
              setCheckCount(value.length);
            }}
          >
            <Space direction="vertical" size="large">
              <Checkbox value="a" className={styles.checkbox}>
                I have added a code of conduct to the rules section of my
                hackathon which all participants, sponsors, organizers, and
                attendees must abide by.
              </Checkbox>
              <Checkbox value="b" className={styles.checkbox}>
                I understand that Hackathon may unpublished this hackathon at
                any time if it violates these statements or the Terms of
                Service.
              </Checkbox>
              {/* <Checkbox value="c" className={styles.checkbox}>
                This is an in-person hackathon, where hackers will be working on
                site and will use Hackathon to submit their projects.{" "}
              </Checkbox> */}
              <Checkbox value="d" className={styles.checkbox}>
                Maker sure you are verified all the prizes before you submit.
                When you submit button below, you are not allowed to edit prizes
                section.
              </Checkbox>
            </Space>
          </Checkbox.Group>
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            disabled={checkCount !== 3 || isLoading || !canPublish}
            loading={isLoading}
            color="orange"
            className="inter"
          >
            {label}
          </Button>
        </Form.Item>
        <i className="text-12">
          Your hackathon will be live immediately, but may be removed if it
          doesn&apos;t meet these criteria.
        </i>
      </Form>
    </Modal>
  );
};

export default memo(PublishHackathonModal);
